// components/ActivityFeed.jsx
//
// Reusable activity timeline component.
// Consumes the array returned by any  GET /:entity/:id/activity  endpoint
// (appointments, tasks, notes) OR the raw Activity model rows returned by
// the activityLogger (type, description, metadata, createdAt, user).
//
// Props
// ─────
//   items        {Array}   – required – activity / event items
//   loading      {boolean} – show skeleton while fetching
//   emptyMessage {string}  – override the default empty-state copy
//   maxHeight    {string}  – tailwind max-h class, e.g. "max-h-[600px]"
//                            defaults to "max-h-[520px]"

import React, { useState } from "react";
import {
  Activity,
  Calendar,
  CheckCircle,
  FileText,
  ArrowRightLeft,
  UserCheck,
  StickyNote,
  Trash2,
  PlusCircle,
  RefreshCw,
  PhoneCall,
  Mail,
  Bell,
  ChevronDown,
  ChevronUp,
  Clock,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelative(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function formatFullDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalise(item) {
  if (
    [
      "email_sent",
      "email_opened",
      "email_clicked",
      "email_replied",
      "call_made",
      "call_received",
      "note_added",
      "task_created",
      "task_completed",
      "appointment_created",
      "appointment_completed",
      "status_changed",
      "assigned",
      "converted",
      "other",
    ].includes(item.type)
  ) {
    return {
      id: item.id,
      kind: item.type,
      title: item.description || item.type.replace(/_/g, " "),
      detail: item.metadata?.changes?.join(" · ") || null,
      date: item.createdAt,
      actor: item.user?.name || item.metadata?.actorName || null,
      avatarUrl: item.user?.profileImageUrl || null,
      raw: item,
    };
  }

  if (item.type === "appointment") {
    return {
      id: item.id,
      kind: "appointment_created",
      title: item.title || "Appointment",
      detail: item.status ? `Status: ${item.status.replace(/_/g, " ")}` : null,
      date: item.startTime || item.createdAt,
      actor: item.assignee?.name || item.creator?.name || null,
      avatarUrl:
        item.assignee?.profileImageUrl || item.creator?.profileImageUrl || null,
      raw: item,
    };
  }

  if (item.type === "task") {
    return {
      id: item.id,
      kind: item.completedAt ? "task_completed" : "task_created",
      title: item.title || item.subject || "Task",
      detail: item.status ? `Status: ${item.status}` : null,
      date: item.completedAt || item.createdAt,
      actor: item.assignedUser?.name || null,
      avatarUrl: item.assignedUser?.profileImageUrl || null,
      raw: item,
    };
  }

  if (item.type === "note") {
    return {
      id: item.id,
      kind: "note_added",
      title: item.title || "Note added",
      detail: item.content ? item.content.slice(0, 120) : null,
      date: item.createdAt,
      actor: item.creator?.name || null,
      avatarUrl: item.creator?.profileImageUrl || null,
      raw: item,
    };
  }

  return {
    id: item.id || Math.random(),
    kind: "other",
    title: item.title || item.description || "Event",
    detail: null,
    date: item.createdAt,
    actor: null,
    avatarUrl: null,
    raw: item,
  };
}

// ─── Icon + colour per activity kind ─────────────────────────────────────────

const KIND_CONFIG = {
  email_sent: { Icon: Mail, dot: "bg-blue-500", label: "Email sent" },
  email_opened: { Icon: Mail, dot: "bg-blue-400", label: "Email opened" },
  email_clicked: { Icon: Mail, dot: "bg-blue-300", label: "Email clicked" },
  email_replied: { Icon: Mail, dot: "bg-blue-600", label: "Email replied" },
  call_made: { Icon: PhoneCall, dot: "bg-green-500", label: "Call made" },
  call_received: {
    Icon: PhoneCall,
    dot: "bg-green-400",
    label: "Call received",
  },
  note_added: { Icon: StickyNote, dot: "bg-yellow-500", label: "Note added" },
  task_created: {
    Icon: PlusCircle,
    dot: "bg-indigo-500",
    label: "Task created",
  },
  task_completed: {
    Icon: CheckCircle,
    dot: "bg-emerald-500",
    label: "Task completed",
  },
  appointment_created: {
    Icon: Calendar,
    dot: "bg-purple-500",
    label: "Appointment",
  },
  appointment_completed: {
    Icon: CheckCircle,
    dot: "bg-purple-400",
    label: "Appointment completed",
  },
  status_changed: {
    Icon: ArrowRightLeft,
    dot: "bg-orange-500",
    label: "Status changed",
  },
  assigned: { Icon: UserCheck, dot: "bg-cyan-500", label: "Assigned" },
  converted: { Icon: RefreshCw, dot: "bg-pink-500", label: "Converted" },
  other: { Icon: Activity, dot: "bg-gray-500", label: "Activity" },
};

function getConfig(kind) {
  return KIND_CONFIG[kind] || KIND_CONFIG.other;
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, url, size = 7 }) {
  const initials = name
    ? name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className={`w-${size} h-${size} rounded-full object-cover ring-1 ring-gray-700`}
      />
    );
  }

  return (
    <div
      className={`w-${size} h-${size} rounded-full bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-300 ring-1 ring-gray-600`}
    >
      {initials}
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow({ isDarkMode }) {
  return (
    <div className="flex gap-4 py-4 animate-pulse">
      <div className="flex flex-col items-center gap-1 pt-1">
        <div
          className={`w-3 h-3 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
        />
        <div
          className={`w-px flex-1 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
        />
      </div>
      <div className="flex-1 pb-2">
        <div
          className={`h-4 rounded w-2/3 mb-2 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
        />
        <div
          className={`h-3 rounded w-1/3 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
        />
      </div>
    </div>
  );
}

// ─── Single timeline row ──────────────────────────────────────────────────────

function TimelineRow({ event, isLast }) {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const { Icon, dot, label } = getConfig(event.kind);

  const hasDetail = event.detail && event.detail.trim().length > 0;
  const isLong = hasDetail && event.detail.length > 80;

  return (
    <div className="flex gap-4 group">
      {/* Timeline spine */}
      <div className="flex flex-col items-center flex-shrink-0 w-6">
        <span
          className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${dot} ring-2 ${
            isDarkMode ? "ring-[#0D0F14]" : "ring-white"
          }`}
        />
        {!isLast && (
          <span
            className={`w-px flex-1 mt-1 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <Icon
              size={14}
              className={`mt-0.5 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
            <div className="min-w-0">
              <p
                className={`text-sm font-medium leading-snug ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {event.title}
              </p>

              {hasDetail && (
                <div className="mt-1">
                  <p
                    className={`text-xs leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"} ${
                      isLong && !expanded ? "line-clamp-2" : ""
                    }`}
                  >
                    {event.detail}
                  </p>
                  {isLong && (
                    <button
                      onClick={() => setExpanded((e) => !e)}
                      className="mt-0.5 flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {expanded ? (
                        <>
                          <ChevronUp size={12} /> Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown size={12} /> Show more
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {event.actor && (
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  by {event.actor}
                </p>
              )}
            </div>
          </div>

          {/* Timestamp */}
          <div className="flex-shrink-0 text-right">
            <span
              className={`text-xs whitespace-nowrap ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              title={formatFullDate(event.date)}
            >
              {formatRelative(event.date)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ActivityFeed({
  items = [],
  loading = false,
  emptyMessage = "No activity recorded yet.",
  maxHeight = "max-h-[520px]",
}) {
  const { isDarkMode } = useTheme();
  const events = items.map(normalise);

  return (
    <div className="flex flex-col h-full">
      {/* Timeline */}
      <div className={`overflow-y-auto ${maxHeight} pr-1`}>
        {loading ? (
          <div>
            {[...Array(5)].map((_, i) => (
              <SkeletonRow key={i} isDarkMode={isDarkMode} />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Activity
              size={36}
              className={`mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
            />
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {emptyMessage}
            </p>
          </div>
        ) : (
          <div>
            {events.map((event, idx) => (
              <TimelineRow
                key={event.id || idx}
                event={event}
                isLast={idx === events.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer count */}
      {!loading && events.length > 0 && (
        <p
          className={`text-xs mt-3 text-right ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
        >
          {events.length} event{events.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}
